import type {
  SpreadsheetChartArtifactType,
  SpreadsheetPasteSpecialMode,
} from '../protocol/spreadsheetEngineProtocol'
import type { SpreadsheetCommandId, SpreadsheetCommandState } from './commandRegistry'
import {
  buildClipboardContextMenuEntries,
  pasteSpecialCommandForAction,
  type ClipboardContextMenuEntry,
} from './clipboardPresentation'
import {
  buildCellMetadataActionPlan,
  buildCellMetadataContextMenuEntries,
  buildCellMetadataRangeContextMenuEntries,
  type CellMetadataActionPlan,
  type CellMetadataProjectionLike,
} from './cellMetadataPresentation'
import {
  buildDerivedArtifactContextMenuEntries,
  derivedArtifactChartMetadataUpdateForContextMenuAction,
  derivedArtifactChartTypeForContextMenuAction,
  type ExcelRangeChartMetadataUpdate,
  type DerivedArtifactContextMenuPresentation,
} from './derivedArtifactPreview'
import {
  buildRowColumnContextMenuEntries,
  type RowColumnContextMenuEntry,
  type RowColumnContextMenuKind,
} from './rowColumnContextMenuPresentation'
import {
  buildTableContextMenuEntries,
  tableStyleIdForContextMenuAction,
  type TableContextMenuPresentation,
} from './autoFilterUi'
import { buildValidationContextMenuEntries } from './validationPresentation'

export type SpreadsheetContextMenuDescriptor =
  | { type: 'separator' }
  | {
    type?: 'item'
    commandId?: SpreadsheetCommandId
    action?: string
    label?: string
    title?: string
    shortcut?: string
    disabled?: boolean
    checked?: boolean
    submenu?: SpreadsheetContextMenuDescriptor[]
  }

type CommandStateProjection = Pick<SpreadsheetCommandState, 'enabled' | 'disabledReason'>

export type SpreadsheetContextMenuContextKind = 'cell' | 'row' | 'column'

export type SpreadsheetTableContextMenuAction =
  | 'resize-table'
  | 'rename-table'
  | 'toggle-table-header-row'
  | 'toggle-table-filter-buttons'
  | 'toggle-table-total-row'
  | 'delete-table'

export type SpreadsheetDerivedArtifactContextMenuAction =
  | 'rename-derived-artifact'
  | 'delete-derived-artifact'
  | 'resize-derived-artifact-to-selection'
  | 'set-derived-artifact-source-to-selection'
  | 'select-derived-artifact-source-range'

export type SpreadsheetContextMenuActionPlan =
  | { kind: 'none' }
  | { kind: 'paste-special'; mode: SpreadsheetPasteSpecialMode }
  | { kind: 'set-derived-chart-type'; chartType: SpreadsheetChartArtifactType }
  | { kind: 'set-derived-chart-metadata'; update: ExcelRangeChartMetadataUpdate }
  | { kind: 'set-table-style'; styleId: string }
  | { kind: 'cell-metadata'; plan: CellMetadataActionPlan; needsContextMetadata: boolean }
  | { kind: 'clipboard'; action: 'cut' | 'copy' | 'paste' }
  | { kind: 'clear-contents' }
  | { kind: 'open-format-cells' }
  | { kind: 'open-validation-settings' }
  | { kind: 'open-sheet-protection' }
  | { kind: 'open-find-dialog'; mode: 'find' | 'replace' }
  | { kind: 'table-action'; action: Exclude<SpreadsheetTableContextMenuAction, 'rename-table'> }
  | { kind: 'rename-table'; promptTitle: string; promptDefaultName: string }
  | { kind: 'derived-artifact-action'; action: Exclude<SpreadsheetDerivedArtifactContextMenuAction, 'rename-derived-artifact'> }
  | { kind: 'rename-derived-artifact'; promptTitle: string; promptDefaultTitle: string }

function isRowColumnContextMenuSeparator(
  entry: RowColumnContextMenuEntry,
): entry is Extract<RowColumnContextMenuEntry, { type: 'separator' }> {
  return 'type' in entry && entry.type === 'separator'
}

function mapClipboardEntry(entry: ClipboardContextMenuEntry): SpreadsheetContextMenuDescriptor {
  if ('type' in entry && entry.type === 'separator') return { type: 'separator' }
  if ('type' in entry && entry.type === 'paste-special-group') {
    return {
      label: entry.label,
      title: entry.title,
      disabled: entry.disabled,
      submenu: entry.commands.map((command) => ({
        commandId: command.id,
        action: command.action,
        disabled: command.disabled,
      })),
    }
  }
  return {
    commandId: entry.id,
    action: entry.action,
  }
}

export function buildSpreadsheetClipboardContextMenuDescriptors(args: {
  hasEngineClipboard?: boolean | null
  disabledPasteSpecialCommandIds?: Iterable<SpreadsheetCommandId>
}): SpreadsheetContextMenuDescriptor[] {
  return buildClipboardContextMenuEntries({
    hasEngineClipboard: args.hasEngineClipboard,
    disabledCommandIds: args.disabledPasteSpecialCommandIds,
  }).map(mapClipboardEntry)
}

function mapPresentationEntry(entry: {
  type?: 'separator' | 'item'
  label?: string
  title?: string
  action?: string
  checked?: boolean
  submenu?: Array<{ label: string; title?: string; action: string; checked?: boolean }>
}): SpreadsheetContextMenuDescriptor {
  if (entry.type === 'separator') return { type: 'separator' }
  return {
    label: entry.label,
    title: entry.title,
    checked: entry.checked,
    action: entry.action,
    submenu: entry.submenu?.map((item) => ({
      label: item.label,
      title: item.title,
      checked: item.checked,
      action: item.action,
    })),
  }
}

function buildValidationContextDescriptors(): SpreadsheetContextMenuDescriptor[] {
  return buildValidationContextMenuEntries().map((entry) => ({
    commandId: entry.commandId,
    action: entry.action,
  }))
}

function buildMetadataContextDescriptors(metadata?: CellMetadataProjectionLike | null): SpreadsheetContextMenuDescriptor[] {
  return buildCellMetadataContextMenuEntries({ metadata }).map((entry) => ({
    commandId: entry.commandId,
    action: entry.action,
    label: entry.label,
    title: entry.title,
  }))
}

function buildRangeMetadataContextDescriptors(): SpreadsheetContextMenuDescriptor[] {
  return buildCellMetadataRangeContextMenuEntries().map((entry) => ({
    commandId: entry.commandId,
    action: entry.action,
  }))
}

export function buildSpreadsheetCellContextMenuDescriptors(args: {
  hasEngineClipboard?: boolean | null
  disabledPasteSpecialCommandIds?: Iterable<SpreadsheetCommandId>
  metadata?: CellMetadataProjectionLike | null
  tableMenu?: TableContextMenuPresentation | null
  derivedArtifactMenu?: DerivedArtifactContextMenuPresentation | null
}): SpreadsheetContextMenuDescriptor[] {
  const tableItems = buildTableContextMenuEntries(args.tableMenu ?? null).map(mapPresentationEntry)
  const artifactItems = buildDerivedArtifactContextMenuEntries(args.derivedArtifactMenu ?? null).map(mapPresentationEntry)
  return [
    ...buildSpreadsheetClipboardContextMenuDescriptors(args),
    { type: 'separator' },
    { commandId: 'clear-contents', action: 'clear-contents' },
    { commandId: 'format-cells', action: 'format-cells' },
    ...tableItems,
    ...artifactItems,
    ...buildValidationContextDescriptors(),
    ...buildMetadataContextDescriptors(args.metadata),
    { type: 'separator' },
    { commandId: 'find', action: 'find' },
    { commandId: 'replace', action: 'replace' },
  ]
}

export function buildSpreadsheetRowColumnContextMenuDescriptors(args: {
  kind: RowColumnContextMenuKind
  count: number
  totalCount: number
  hasStructuralActions: boolean
  insertState: CommandStateProjection
  deleteState: CommandStateProjection
  formatState: CommandStateProjection
  resizeBlockedByProtection: boolean
  resizeProtectionReason?: string | null
  hasEngineClipboard?: boolean | null
  disabledPasteSpecialCommandIds?: Iterable<SpreadsheetCommandId>
}): SpreadsheetContextMenuDescriptor[] {
  const rowColumnEntries = buildRowColumnContextMenuEntries({
    kind: args.kind,
    count: args.count,
    totalCount: args.totalCount,
    hasStructuralActions: args.hasStructuralActions,
    insertState: args.insertState,
    deleteState: args.deleteState,
    formatState: args.formatState,
    resizeBlockedByProtection: args.resizeBlockedByProtection,
    resizeProtectionReason: args.resizeProtectionReason,
  })
  const menuItems: SpreadsheetContextMenuDescriptor[] = []
  for (const entry of rowColumnEntries) {
    if (isRowColumnContextMenuSeparator(entry)) {
      menuItems.push({ type: 'separator' })
      continue
    }
    menuItems.push({
      commandId: entry.commandId,
      action: entry.action,
      label: entry.label,
      title: entry.title,
      shortcut: entry.shortcut,
      disabled: entry.disabled,
    })
    if (entry.action === 'format-cells') {
      menuItems.push(...buildValidationContextDescriptors(), ...buildRangeMetadataContextDescriptors())
    }
  }
  return [
    ...buildSpreadsheetClipboardContextMenuDescriptors(args),
    { type: 'separator' },
    ...menuItems,
  ]
}

export function buildSpreadsheetContextMenuActionPlan(args: {
  action: string
  contextKind?: SpreadsheetContextMenuContextKind | null
  tableMenu?: TableContextMenuPresentation | null
  derivedArtifactMenu?: DerivedArtifactContextMenuPresentation | null
}): SpreadsheetContextMenuActionPlan {
  const action = String(args.action || '').trim()
  const contextKind = args.contextKind ?? 'cell'
  const pasteSpecialCommand = pasteSpecialCommandForAction(action)
  if (pasteSpecialCommand) return { kind: 'paste-special', mode: pasteSpecialCommand.mode }

  const chartType = derivedArtifactChartTypeForContextMenuAction(action)
  if (chartType) return { kind: 'set-derived-chart-type', chartType }

  const chartMetadataUpdate = derivedArtifactChartMetadataUpdateForContextMenuAction(action)
  if (chartMetadataUpdate) return { kind: 'set-derived-chart-metadata', update: chartMetadataUpdate }

  const tableStyleId = tableStyleIdForContextMenuAction(action)
  if (tableStyleId) return { kind: 'set-table-style', styleId: tableStyleId }

  const metadataActionPlan = buildCellMetadataActionPlan(action)
  if (metadataActionPlan) {
    return {
      kind: 'cell-metadata',
      plan: metadataActionPlan,
      needsContextMetadata: metadataActionPlan.action === 'open-hyperlink',
    }
  }

  switch (action) {
    case 'cut':
    case 'copy':
    case 'paste':
      return { kind: 'clipboard', action }
    case 'clear-contents':
      return { kind: 'clear-contents' }
    case 'format-cells':
      return { kind: 'open-format-cells' }
    case 'data-validation':
      return { kind: 'open-validation-settings' }
    case 'protect-sheet':
      return { kind: 'open-sheet-protection' }
    case 'find':
    case 'replace':
      return { kind: 'open-find-dialog', mode: action }
    case 'resize-table':
    case 'toggle-table-header-row':
    case 'toggle-table-filter-buttons':
    case 'toggle-table-total-row':
    case 'delete-table':
      return contextKind === 'cell'
        ? { kind: 'table-action', action }
        : { kind: 'none' }
    case 'rename-table':
      return contextKind === 'cell'
        ? {
          kind: 'rename-table',
          promptTitle: args.tableMenu?.promptTitle ?? 'Table name',
          promptDefaultName: args.tableMenu?.promptDefaultName ?? '',
        }
        : { kind: 'none' }
    case 'delete-derived-artifact':
    case 'resize-derived-artifact-to-selection':
    case 'set-derived-artifact-source-to-selection':
    case 'select-derived-artifact-source-range':
      return contextKind === 'cell'
        ? { kind: 'derived-artifact-action', action }
        : { kind: 'none' }
    case 'rename-derived-artifact':
      return contextKind === 'cell'
        ? {
          kind: 'rename-derived-artifact',
          promptTitle: args.derivedArtifactMenu?.promptTitle ?? 'Artifact title',
          promptDefaultTitle: args.derivedArtifactMenu?.promptDefaultTitle ?? 'Chart',
        }
        : { kind: 'none' }
    default:
      return { kind: 'none' }
  }
}
